import { cn } from '@/lib/helpers/utils';
import { Link } from 'gatsby';
import { GatsbyImage, StaticImage } from 'gatsby-plugin-image';
import { omit } from 'lodash';
import React from 'react';
import { removeNonAlpaChars } from '../../app/utils/string';
import { asyncTrackingEvent, asyncTrackingEventProps } from '../../app/utils/tracking';
import { BookmarkType, CardInfo, CardVariants, ImageType, ImageTypes } from '../../types/cards';
import Follow from '../bookmarks/Follow';
import Save from '../bookmarks/Save';

interface Props {
  card: CardInfo;
  classes?: string;
}

export const getPlaceHolderImage = (card: CardInfo) => {
  let placeholder: any;
  const attributes: any = {
    layout: 'constrained',
    width: 650
  };

  switch (card.type) {
    case 'article':
    case 'advice':
      placeholder = card.data?.variant ? (
        <StaticImage
          src={`../../assets/images/global/placeholder/featured/article.png`}
          alt={`Image for ${card.reference}`}
          {...attributes}
        />
      ) : (
        <StaticImage
          src={`../../assets/images/global/placeholder/card/article.jpg`}
          alt={`Image for ${card.reference}`}
          {...attributes}
        />
      );
      break;
    case 'organisation':
      placeholder = card.data?.variant ? (
        <StaticImage
          src={`../../assets/images/global/placeholder/featured/organisation.jpg`}
          alt={`Image for ${card.reference}`}
          {...attributes}
        />
      ) : (
        <StaticImage
          src={`../../assets/images/global/placeholder/card/organisation.jpg`}
          alt={`Image for ${card.reference}`}
          {...attributes}
        />
      );
      break;
    case 'education_provider':
      placeholder = card.data?.variant ? (
        <StaticImage
          src={`../../assets/images/global/placeholder/featured/education-provider.jpg`}
          alt={`Image for ${card.reference}`}
          {...attributes}
        />
      ) : (
        <StaticImage
          src={`../../assets/images/global/placeholder/card/education-provider.jpg`}
          alt={`Image for ${card.reference}`}
          {...attributes}
        />
      );
      break;
    case 'event':
      placeholder = (
        <StaticImage
          src={`../../assets/images/global/placeholder/card/event.png`}
          alt={`Image for ${card.reference}`}
          {...attributes}
        />
      );
      break;
    case 'video':
      placeholder = (
        <StaticImage
          src={`../../assets/images/global/placeholder/card/video.jpg`}
          alt={`Image for ${card.reference}`}
          {...attributes}
        />
      );
      break;
    case 'graduate_profile':
      placeholder = card.data?.variant ? (
        <StaticImage
          src={`../../assets/images/global/placeholder/featured/graduate-profile.jpg`}
          alt={`Image for ${card.reference}`}
          {...attributes}
        />
      ) : (
        <StaticImage
          src={`../../assets/images/global/placeholder/card/graduate-profile.png`}
          alt={`Image for ${card.reference}`}
          {...attributes}
        />
      );
      break;
  }

  return placeholder && placeholder.props && placeholder.props.__imageData
    ? {
        image: placeholder.props.__imageData,
        type: ImageTypes.gatsby,
        alt: `Image for ${card.reference}`
      }
    : null;
};

const getImage = (imageInfo: ImageType | null, imageClasses: string = '', imageSrcClasses: string = '') => {
  if (!imageInfo) {
    return null;
  }
  const regexChecker = /.*\/(null|undefined|)$/gm;
  switch (imageInfo.type) {
    case ImageTypes.gatsby:
      return (
        imageInfo.image?.images?.fallback?.src &&
        !(imageInfo.image?.images?.fallback?.src as string).match(regexChecker) && (
          <GatsbyImage
            className={imageClasses}
            imgClassName={imageSrcClasses}
            image={imageInfo.image}
            alt={imageInfo.alt ?? ''}
          />
        )
      );
    case ImageTypes.img:
      return (
        !(imageInfo.image as string).match(regexChecker) && (
          <img
            src={imageInfo.image}
            alt={imageInfo.alt}
            loading="lazy"
            className={`${imageClasses} ${imageSrcClasses}`}
          />
        )
      );
    case ImageTypes.html:
      return <div className={`relative ${imageClasses}`}>{imageInfo.image}</div>;
  }
  return null;
};

const renderSlots = (card: CardInfo) => {
  const imageSlots = ['thumbnail', 'logo', 'cohortLogo'];
  return Object.entries(card.slots ?? {}).map((slot: any, index: number) => {
    const slotKey: string = slot[0];
    const slotValue: string = slot[1];
    const slotClassName: string = card.config?.classNames?.[slotKey] ?? '';
    if (
      slotValue &&
      slotValue !== 'null' &&
      (card.config?.excludes?.indexOf(slotKey) === -1 || !card.config?.excludes)
    ) {
      if (imageSlots.indexOf(slotKey) > -1) {
        return (
          <div
            key={`${slotKey}_${index}`}
            slot={slotKey}
            className={`aspect-auto isolate`}
            style={{ transform: 'translateZ(0)' }}
          >
            {slotValue}
          </div>
        );
      } else {
        return (
          <div
            key={`${slotKey}_${index}`}
            slot={slotKey}
            className={slotClassName}
            dangerouslySetInnerHTML={{ __html: slotValue }}
          ></div>
        );
      }
    }
  });
};

const generateImages = (card: CardInfo) => {
  const banner = getImage(
    card.images?.banner,
    card.config?.images?.banner?.classes ?? 'object-cover min-h-[11rem]',
    card.config?.images?.banner?.srcClasses ?? 'rounded-t-lg rounded-b-none'
  );
  const thumbnail = getImage(
    card.images?.thumbnail,
    card.config?.images?.thumbnail?.classes ?? 'object-cover min-h-[11rem]',
    card.config?.images?.thumbnail?.srcClasses ?? 'rounded-t-lg rounded-b-none'
  );
  const cohortLogo = getImage(
    card.images?.cohortLogo,
    card.config?.images?.cohortLogo?.classes ?? 'min-h-[2.5rem]',
    card.config?.images?.cohortLogo?.srcClasses ?? ''
  );
  const logo = getImage(
    card.images?.logo,
    card.config?.images?.logo?.classes ?? 'min-h-[3rem]',
    card.config?.images?.logo?.srcClasses ?? ''
  );

  !card.slots && (card.slots = {});
  !thumbnail &&
    !banner &&
    !card.attributes.disableImages &&
    (card.slots.thumbnail = getImage(
      getPlaceHolderImage(card),
      card.config?.images?.thumbnail?.classes ?? 'object-cover min-h-[11rem]',
      card.config?.images?.thumbnail?.srcClasses ?? 'rounded-t-lg rounded-b-none'
    ));
  !thumbnail && banner && (card.slots.thumbnail = banner);
  thumbnail && (card.slots.thumbnail = thumbnail);
  logo && (card.slots.logo = logo);
  cohortLogo && (card.slots.cohortLogo = cohortLogo);
};

export const getCardLabelByType = (type: string, asString: boolean = true, overrideLabel?: string) => {
  const label = (className: string, label: string) => {
    if (asString)
      return `<div class="flex flex-row space-x-1.5 items-center"><span class="block rounded-full h-3 w-3 ${className}"></span><span class="block uppercase text-gray-700 text-xs font-semibold tracking-wide">${
        overrideLabel ? overrideLabel : label
      }</span></div>`;
    else {
      return (
        <div className="flex flex-row space-x-1.5 items-center">
          <span className={`block rounded-full h-3 w-3 ${className}`}></span>
          <span className="block uppercase text-gray-700 text-xs font-semibold tracking-wide">
            {overrideLabel ? overrideLabel : label}
          </span>
        </div>
      );
    }
  };

  switch (type) {
    case 'article':
    case 'advice':
      return label('bg-primary-400', 'Advice');
    case 'opportunity':
      return label('bg-mint', 'Opportunity');
    case 'course':
      return label('bg-mint', 'Course');
    case 'organisation':
      return label('bg-deep-400', 'Organisation');
    case 'education_provider':
      return label('bg-deep-400', 'Course provider');
    case 'event':
      return label('bg-banana', 'Event');
    case 'video':
      return label('bg-shock', 'Video');
    case 'pathway':
      return label('bg-shock', 'Pathway');
    case 'graduate_profile':
      return label('bg-primary-400', overrideLabel!);
    default:
  }
};

/**
 * Renders out an individual Card.
 * @param {any} param0
 * @return {any}
 */
export default function Card({ card, classes }: Props) {
  const attributes = card.attributes;
  // Generate the images
  generateImages(card);

  // Tracking
  const trackingEvent = () => {
    const event: asyncTrackingEventProps = {
      track: 'card_click',
      trackAbacus: 'card_click',
      trackValue: card.type,
      trackText: card.reference
    };
    card.id && (event.trackId = card.id);
    if (card.path) {
      if (card.path.indexOf('http') === -1) {
        event.trackUrl = card.path;
      } else {
        event.trackTarget = card.path;
      }
    }
    if (card.data && card.data.externalUrl && card.data.url) {
      event.trackTarget = card.data.url;
    }
    asyncTrackingEvent(event);
  };
  // Depending on cardType
  let cardHtml;
  const allowDirectCardClick = card.type == 'video';
  if (card.data.variant) {
    const CardVariant: any = (CardVariants as any)[card.data.variant];
    cardHtml = (
      <CardVariant
        key={card.index}
        card={card}
        onClick={
          allowDirectCardClick
            ? (event: any) => {
                trackingEvent();
                card.onClick && card.onClick(event);
              }
            : null
        }
        attributes={attributes}
      />
    );
  } else {
    cardHtml = (
      <card.component
        onChilliClick={
          allowDirectCardClick
            ? (event: any) => {
                !attributes.suppressTracking && trackingEvent();
                card.onClick && card.onClick(event);
              }
            : null
        }
        {...omit(attributes, card.config?.excludes ?? [])}
      >
        {/* Render out the slots */}
        {renderSlots(card)}
        {/* Render out the actions */}
        {card.id && !attributes.disableActions && (
          <div slot="actions">
            {card.bookmarkType === BookmarkType.Save ? (
              <div
                className={`${attributes.iconOnlyActions ? 'min-w-[18px]' : ''} ${
                  attributes.disableBorder && attributes.disableShadow ? '-mr-4' : ''
                } relative inline-flex self-stretch flex-shrink-0`}
              >
                <Save id={card.id} icon bookmarkBtnId={card.data.bookmarkBtnId} contentType={card.data.type} />
              </div>
            ) : (
              <div
                className={`${attributes.iconOnlyActions ? 'min-w-[18px]' : ''} ${
                  attributes.disableBorder && attributes.disableShadow ? '-mr-4' : ''
                } relative inline-flex self-stretch flex-shrink-0`}
              >
                <Follow id={card.id} bookmarkBtnId={card.data.bookmarkBtnId} icon />
              </div>
            )}
          </div>
        )}
      </card.component>
    );
  }

  // Set card path to false if we don't want the card to be clickable
  if (card.attributes.target == 'none') {
    card.path = false;
  }
  const hover = 'status' in card.data ? card.data.status : true;
  const cardLink: any =
    card.path && card.path.indexOf('http') === -1 ? (
      card.type !== 'pathway' ? (
        card?.attributes?.target == '_blank' ? (
          <a
            className={`focus:outline-none block ${!attributes.fullHeight ? '' : 'h-full'}`}
            href={card.path}
            key={card.index}
            target="_blank"
            onClick={(event: any) => {
              !attributes.suppressTracking && trackingEvent();
              card.onClick && card.onClick(event);
            }}
            data-cy={`card:view-${removeNonAlpaChars(card.type ?? 'entity')}`}
          >
            {cardHtml}
          </a>
        ) : (
          <Link
            className={`focus:outline-none block ${!attributes.fullHeight ? '' : 'h-full'}`}
            to={card.path}
            key={card.index}
            onClick={(event: any) => {
              !attributes.suppressTracking && trackingEvent();
              card.onClick && card.onClick(event);
            }}
            data-cy={`card:view-${removeNonAlpaChars(card.type ?? 'entity')}`}
          >
            {cardHtml}
          </Link>
        )
      ) : (
        // Temp H@ck till we get a reload config option in the pathways plugin
        <a
          className={`focus:outline-none block ${!attributes.fullHeight ? '' : 'h-full'}`}
          href={card.path}
          key={card.index}
          onClick={(event: any) => {
            !attributes.suppressTracking && trackingEvent();
            card.onClick && card.onClick(event);
          }}
          data-cy={`card:view-${removeNonAlpaChars(card.type)}`}
        >
          {cardHtml}
        </a>
      )
    ) : (
      <a
        className={`focus:outline-none block ${!attributes.fullHeight ? '' : 'h-full'}`}
        href={card.path as string}
        key={card.index}
        target="_blank"
        rel="noreferrer"
        onClick={(event: any) => {
          !attributes.suppressTracking && trackingEvent();
          card.onClick && card.onClick(event);
        }}
        data-cy={`card:view-${removeNonAlpaChars(card.type ?? 'entity')}-site`}
      >
        {cardHtml}
      </a>
    );

  return (
    <div
      className={cn(
        'group relative focus-within:ring-2 focus-within:ring-primary-400 focus-within:ring-offset-2 focus-within:rounded-lg focus:outline-none',
        !attributes.fullHeight ? '' : 'h-full',
        hover && 'cursor-pointer',
        classes ?? '',
        'focus-within:ring-offset-0 focus-within:rounded-2xl focus-within:ring-1'
      )}
    >
      {card.path ? cardLink : cardHtml}
    </div>
  );
}
